import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { CircularProgress } from '@mui/material';

import { CoreBox, CoreTypography } from '@youscience/core';

import { CollegeLocation } from '@interfaces/location';

import useFavoriteMajorsStore from '@store/FavoriteMajorsStore';
import useHomePageStore from '@store/HomePageStore';
import useUserPreferencesStore from '@store/UserPreferencesStore';

import { Breadcrumbs } from '@components/Breadcrumbs';
import { Container } from '@components/Layout/components/Container';
import { MajorCard } from '@components/MajorCard';
import { MajorCardSkeleton } from '@components/MajorCard/MajorCardSkeleton';
import { EmptyStateText } from '@components/EmptyStateText';

import { ROUTES } from '@constants/ROUTES';
import { BREADCRUMBS_PAGE_TEXT } from '@constants/breadcrumbs';

import { getBreadcrumbs } from '@utils/breadcrumbs';

import FilledHeartIcon from '@images/heart-filled.svg?react';
import MediumTargetIcon from '@images/medium-target.svg?react';

import {
  sxStyles,
  StyledNavBar,
  StyledNavBarTitle,
  StyledExploreByTitle,
  StyledMajorsLink,
  StyledSchoolsLink,
  StyledSwitcher,
  StyledSwitcherWrap,
  StyledFavoritesLink,
  StyledMajorsCardsWrap,
  StyledProgressWrap,
} from './FavoriteMajorList.styles';

export const FavoriteMajorList = () => {
  const location: CollegeLocation = useLocation();

  const { cipCodes: favoriteMajorsCipCodes } = useUserPreferencesStore((state) => state.majorFavorites);
  const { favoriteMajors, unfavoritingMajors, isFavoriteMajorsLoading } = useFavoriteMajorsStore((state) => ({
    favoriteMajors: state.favoriteMajors,
    unfavoritingMajors: state.unfavoritingMajors,
    isFavoriteMajorsLoading: state.isFavoriteMajorsLoading,
  }));
  const selectedSchoolName = useHomePageStore((state) => state.landingPageSchoolData?.cardInfo?.name);

  const getMajorsByCipCodes = useFavoriteMajorsStore((state) => state.getMajorsByCipCodes);

  const pageBreadcrumbs = getBreadcrumbs(
    location.state?.from,
    BREADCRUMBS_PAGE_TEXT.FAVORITE_MAJORS,
    selectedSchoolName,
  );

  useEffect(() => {
    void getMajorsByCipCodes(favoriteMajorsCipCodes);
  }, [favoriteMajorsCipCodes, getMajorsByCipCodes]);

  const loadedMajorCipCodes = new Set(favoriteMajors?.majors?.map((major) => major.cipCode) || []);
  const unloadedCipCodes = favoriteMajorsCipCodes.filter(
    (cipCode) => !loadedMajorCipCodes.has(cipCode) && !unfavoritingMajors.has(cipCode),
  );

  return (
    <CoreBox sx={sxStyles.schoolsBg} data-testid="favorite-majors-list-page">
      <CoreBox sx={sxStyles.headSectionWrap}>
        <Container sx={sxStyles.headSectionInner}>
          <Breadcrumbs links={pageBreadcrumbs.links} currentPage={pageBreadcrumbs.currentPage} />

          <StyledNavBar>
            <CoreBox sx={sxStyles.navBarInfo}>
              <StyledNavBarTitle variant="h2" component="h1">
                Favorites
              </StyledNavBarTitle>

              <CoreTypography>
                Your talents are needed! Put your natural talents to work by checking out these awesome majors and
                programs.
              </CoreTypography>
            </CoreBox>

            {!selectedSchoolName && (
              <StyledSwitcherWrap>
                <StyledExploreByTitle component="div">Explore by:</StyledExploreByTitle>

                <StyledSwitcher>
                  <StyledSchoolsLink
                    to={ROUTES.FAVORITE_SCHOOLS}
                    state={{
                      from: [{ to: ROUTES.SCHOOLS, text: BREADCRUMBS_PAGE_TEXT.SCHOOLS }],
                    }}
                  >
                    SCHOOLS
                  </StyledSchoolsLink>

                  <StyledMajorsLink to={ROUTES.FAVORITE_MAJORS}>MAJORS</StyledMajorsLink>
                </StyledSwitcher>
              </StyledSwitcherWrap>
            )}
          </StyledNavBar>

          <CoreBox sx={sxStyles.actionPanel}>
            <StyledFavoritesLink
              to={ROUTES.MAJORS}
              state={{
                from: [{ to: ROUTES.FAVORITE_MAJORS, text: BREADCRUMBS_PAGE_TEXT.FAVORITE_MAJORS }],
              }}
            >
              <FilledHeartIcon />
              Favorites ({favoriteMajorsCipCodes?.length || 0})
            </StyledFavoritesLink>
          </CoreBox>
        </Container>
      </CoreBox>

      {isFavoriteMajorsLoading ? (
        <StyledProgressWrap>
          <CircularProgress />
        </StyledProgressWrap>
      ) : null}

      <Container sx={sxStyles.mainSectionWrap} data-testid="favorite-majors-list">
        <StyledMajorsCardsWrap data-testid="favorite-majors-list-cards">
          {favoriteMajors?.majors?.map((major) => (
            <MajorCard key={major.cipCode} major={major} componentId="favorite-majors-list-card" />
          ))}

          {unloadedCipCodes.map((cipCode, index) => (
            <MajorCardSkeleton key={`skeleton-${cipCode}-${index}`} />
          ))}
        </StyledMajorsCardsWrap>
      </Container>

      {favoriteMajorsCipCodes.length === 0 && !isFavoriteMajorsLoading && (
        <EmptyStateText
          icon={<MediumTargetIcon />}
          title="No favorite majors yet"
          subtitle="Hmmm. Looks like you haven't favorited any programs."
          buttonTitle="Start exploring"
          buttonLink={ROUTES.MAJORS}
        />
      )}
    </CoreBox>
  );
};
